/**
 * Admin Layout - Main navigation and structure
 */

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  UtensilsCrossed, 
  Users, 
  Calendar,
  ShoppingCart,
  Package,
  BarChart3,
  Settings,
  Menu as MenuIcon,
  X,
  LogOut,
  ChevronDown,
  Building2,
  Bell,
  Search,
  Layers,
  CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useBranch } from './context/BranchContext';
import { useAuth } from './context/AuthContext';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { 
    name: 'Menu', 
    href: '/menu', 
    icon: UtensilsCrossed,
    children: [
      { name: 'All Items', href: '/menu' },
      { name: 'AI Import', href: '/menu/import' },
    ]
  },
  { 
    name: 'Staff', 
    href: '/staff', 
    icon: Users,
    children: [
      { name: 'Employees', href: '/staff' },
      { name: 'Schedule', href: '/staff/schedule' },
    ]
  },
  { name: 'Orders', href: '/orders', icon: ShoppingCart },
  { 
    name: 'Inventory', 
    href: '/inventory', 
    icon: Package,
    children: [
      { name: 'Cycle Count', href: '/inventory/cycle-count' },
    ]
  },
  { name: 'Reports', href: '/reports', icon: BarChart3 },
  { name: 'App Links', href: '/apps', icon: Layers },
  { name: 'Payment Config', href: '/payment-config', icon: CreditCard },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export default function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentBranch, branches, switchBranch } = useBranch();
  const { user, logout } = useAuth();

  const toggleExpand = (name) => {
    setExpandedItems(prev => 
      prev.includes(name) 
        ? prev.filter(n => n !== name)
        : [...prev, name]
    );
  };

  // Get the base path for the admin app (could be /admin or /pwa/admin)
  const getBasePath = () => {
    const path = location.pathname;
    if (path.includes('/pwa/admin')) return '/pwa/admin';
    if (path.includes('/admin')) return '/admin';
    return '';
  };

  const basePath = getBasePath();

  const isActive = (href) => {
    const fullPath = `${basePath}${href}`;
    return location.pathname === fullPath || location.pathname.startsWith(fullPath + '/');
  };

  // Helper to get full path for navigation links
  const getFullPath = (href) => `${basePath}${href}`;

  const handleLogout = () => {
    logout();
    navigate(`${basePath}/login`);
  };

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100">{/* dark: the shared back-office pages it shows use dark: variants */}
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 border-r border-slate-800 transform transition-transform duration-300 lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          <Link to={getFullPath('/dashboard')} className="flex items-center gap-2">
            <img src="/chain29-logo.png" alt="Chain29" className="w-8 h-8 rounded-lg object-cover" />
            <span className="text-white font-semibold">Chain29</span>
          </Link>
          <Button 
            variant="ghost" 
            size="icon" 
            className="lg:hidden text-slate-400"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Branch Selector */}
        {branches && branches.length > 1 && (
          <div className="p-3 border-b border-slate-800">
            <select
              value={currentBranch?.id || ''}
              onChange={(e) => switchBranch(e.target.value)}
              className="w-full bg-slate-800 text-white text-sm rounded-lg px-3 py-2 border border-slate-700 focus:border-emerald-500 focus:outline-none"
            >
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navigation.map((item) => (
            <div key={item.name}>
              {item.children ? (
                <>
                  <button
                    onClick={() => toggleExpand(item.name)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                      isActive(item.href)
                        ? "bg-emerald-600/20 text-emerald-400"
                        : "text-slate-400 hover:bg-slate-800 hover:text-white"
                    )}
                  >
                    <span className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </span>
                    <ChevronDown className={cn(
                      "w-4 h-4 transition-transform",
                      expandedItems.includes(item.name) && "rotate-180"
                    )} />
                  </button>
                  {expandedItems.includes(item.name) && (
                    <div className="ml-8 mt-1 space-y-1">
                      {item.children.map(child => (
                        <Link
                          key={child.href}
                          to={getFullPath(child.href)}
                          className={cn(
                            "block px-3 py-2 rounded-lg text-sm transition-colors",
                            location.pathname === getFullPath(child.href)
                              ? "bg-emerald-600/10 text-emerald-400"
                              : "text-slate-500 hover:bg-slate-800 hover:text-white"
                          )}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={getFullPath(item.href)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                    isActive(item.href)
                      ? "bg-emerald-600/20 text-emerald-400"
                      : "text-slate-400 hover:bg-slate-800 hover:text-white"
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  {item.name}
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* User section */}
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {user?.email || ''}
              </p>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              className="text-slate-400 hover:text-white"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-14 sm:h-16 bg-slate-900/95 backdrop-blur border-b border-slate-800 flex items-center justify-between px-3 sm:px-4 lg:px-6">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden text-slate-400 h-9 w-9"
              onClick={() => setSidebarOpen(true)}
            >
              <MenuIcon className="w-5 h-5" />
            </Button>
            
            {/* Mobile: Just logo text */}
            <span className="lg:hidden text-white font-semibold text-sm">Chain29</span>
            
            {/* Search (desktop only) */}
            <div className="hidden md:flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-2">
              <Search className="w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Search..."
                className="bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none w-48"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Notifications */}
            <Button variant="ghost" size="icon" className="text-slate-400 relative h-9 w-9">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-emerald-500 rounded-full" />
            </Button>

            {/* Current branch indicator - hidden on very small screens */}
            {currentBranch && (
              <div className="hidden sm:flex items-center gap-2 text-sm text-slate-400">
                <Building2 className="w-4 h-4" />
                <span className="max-w-[120px] truncate">{currentBranch.name}</span>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="p-3 sm:p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
